<template lang="pug">
    .field-card(:class="{ 'is-locked': isLocked, 'is-expanded': expanded }")
        //- Collapsed by default: one line per question keeps a long form readable.
        .field-card-head(@click="expanded = !expanded")
            .field-card-grip(@click.stop :title="$t('Drag to reorder')")
                i.el-icon-fa-grip-vertical.fas
            .field-card-toggle
                i.el-icon-fa-chevron-right.fas
            .field-card-type
                i.field-card-icon(:class="`el-icon-fa-${typeIcon} fas`")
                .field-card-text
                    .field-card-label {{ field.label || $t(`field_type.${field.type}`) }}
                    .field-card-meta {{ meta }}
            .field-card-actions(@click.stop)
                el-tooltip(effect="dark" :content="$t('Move up')" placement="top")
                    el-button.action(type="text" icon="el-icon-fa-arrow-up fas" :disabled="index === 0" @click="move(-1)")
                el-tooltip(effect="dark" :content="$t('Move down')" placement="top")
                    el-button.action(type="text" icon="el-icon-fa-arrow-down fas" :disabled="index === total - 1" @click="move(1)")
                el-tooltip(v-if="isRemovable" effect="dark" :content="$t('Remove field')" placement="top")
                    el-button.action.danger(type="text" icon="el-icon-fa-trash-alt far" @click="remove")
            el-tooltip(effect="dark" :content="$t('Width')" placement="top")
                el-radio-group.width-control(size="mini" :value="field.width || 'full'" @input="update('width', $event)" @click.native.stop)
                    el-radio-button(v-for="option in fieldWidths" :key="option.width" :label="option.width") {{ option.label }}

        .field-card-body(v-show="expanded")
            el-row(:gutter="20")
                el-col(:lg="14" :md="14")
                    el-form-item.compact(:label="$t('Label')")
                        el-input(size="small" :value="field.label" maxlength="255" @input="update('label', $event)")
                el-col(:lg="10" :md="10")
                    el-form-item.compact(:label="$t('Type')")
                        el-select.is-full-width(size="small" :value="field.type" :disabled="isFixed" @change="update('type', $event)")
                            el-option(
                                v-for="fieldType in fieldTypes"
                                :key="fieldType.type"
                                :label="$t(`field_type.${fieldType.type}`)"
                                :value="fieldType.type"
                            )

            el-row(:gutter="20" v-if="hasPlaceholder")
                el-col(:lg="14" :md="14")
                    el-form-item.compact(:label="$t('Placeholder')")
                        el-input(size="small" :value="field.placeholder || ''" maxlength="255" @input="update('placeholder', $event)")
                el-col(:lg="10" :md="10")
                    el-form-item.compact(:label="$t('Help text')")
                        el-input(size="small" :value="field.help_text || ''" maxlength="500" @input="update('help_text', $event)")
            el-form-item.compact(v-else :label="$t('Help text')")
                el-input(size="small" :value="field.help_text || ''" maxlength="500" @input="update('help_text', $event)")

            el-form-item.compact(v-if="hasOptions" :label="$t('Options')")
                .option-list
                    .option-row(v-for="(option, optionIndex) in field.options" :key="option.id")
                        i.option-marker(:class="optionMarkerClass")
                        el-input(
                            size="small"
                            :value="option.label"
                            maxlength="255"
                            :placeholder="$t('Option {number}', { number: optionIndex + 1 })"
                            @input="updateOption(optionIndex, $event)"
                        )
                        el-button(
                            circle size="mini" icon="el-icon-fa-times fas"
                            :disabled="field.options.length <= 1"
                            @click="removeOption(optionIndex)"
                        )
                    el-button.add-option(size="mini" plain icon="el-icon-fa-plus fas" @click="addOption") {{ $t('Add option') }}

            el-form-item.compact.no-margin
                el-switch(
                    :value="!!field.required"
                    :active-text="$t('Required')"
                    :disabled="isAlwaysRequired"
                    @change="update('required', $event)"
                )
</template>

<i18n>
{
  "en": {
    "Option {number}": "Option {number}",
    "fixed_field.salutation": "Salutation",
    "fixed_field.first_name": "First name",
    "fixed_field.last_name": "Last name",
    "fixed_field.email": "Email",
    "fixed_field.phone": "Phone",
    "fixed_field.slot": "Event slot",
    "field_type.text": "Text",
    "field_type.textarea": "Text area",
    "field_type.number": "Number",
    "field_type.date": "Date",
    "field_type.time": "Time",
    "field_type.select": "Dropdown",
    "field_type.radio": "Radio buttons",
    "field_type.checkbox": "Checkbox",
    "field_type.checkbox_group": "Checkboxes"
  },
  "de": {
    "Width": "Breite",
    "Drag to reorder": "Ziehen zum Sortieren",
    "Move up": "Nach oben",
    "Move down": "Nach unten",
    "Remove field": "Feld entfernen",
    "Label": "Bezeichnung",
    "Type": "Typ",
    "Placeholder": "Platzhalter",
    "Help text": "Hilfetext",
    "Options": "Optionen",
    "Option {number}": "Option {number}",
    "Add option": "Option hinzufügen",
    "Required": "Pflichtfeld",
    "fixed": "fest",
    "fixed_field.salutation": "Anrede",
    "fixed_field.first_name": "Vorname",
    "fixed_field.last_name": "Nachname",
    "fixed_field.email": "E-Mail",
    "fixed_field.phone": "Telefon",
    "fixed_field.slot": "Termin",
    "field_type.text": "Textfeld",
    "field_type.textarea": "Textbereich",
    "field_type.number": "Zahl",
    "field_type.date": "Datum",
    "field_type.time": "Uhrzeit",
    "field_type.select": "Dropdown",
    "field_type.radio": "Optionsfelder",
    "field_type.checkbox": "Checkbox",
    "field_type.checkbox_group": "Checkboxen"
  }
}
</i18n>

<script>

import _ from 'lodash'
import { useEventManagerRegistrationTemplateStore } from '@/stores'
import { FIELD_TYPES, FIELD_TYPES_WITH_OPTIONS, FIELD_WIDTHS, FIXED_ALWAYS_REQUIRED, FIXED_FIELDS, isFixedField, isRemovableField } from '@/stores/eventManagerRegistrationTemplate'

export default {
    name: 'FieldCard',
    props: {
        field: { type: Object, required: true },
        index: { type: Number, required: true },
        total: { type: Number, required: true },
    },
    data() {
        return {
            fieldTypes: FIELD_TYPES,
            fieldWidths: FIELD_WIDTHS,
            // A freshly added custom field has no label yet and opens right away; everything
            // loaded from the server starts collapsed.
            expanded: !isFixedField(this.field) && !this.field.label,
        }
    },
    computed: {
        registrationTemplateStore() {
            return useEventManagerRegistrationTemplateStore();
        },
        // A mandatory fixed field shows a lock; an optional fixed one (removable) its own icon.
        typeIcon() {
            if (this.isLocked) return 'lock'
            if (this.isFixed) {
                const fixed = _.find(FIXED_FIELDS, { key: this.field.key })
                if (fixed) return fixed.icon
            }
            const fieldType = _.find(FIELD_TYPES, { type: this.field.type })
            return fieldType ? fieldType.icon : 'font'
        },
        isLocked() {
            return this.isFixed && !this.isRemovable
        },
        isFixed() {
            return isFixedField(this.field)
        },
        // One line under the label: kind of question, width if not the whole row, required.
        meta() {
            const parts = [this.isFixed ? this.$t(`fixed_field.${this.field.key}`) : this.$t(`field_type.${this.field.type}`)]
            if (this.isLocked) parts.push(this.$t('fixed'))
            const width = _.find(this.fieldWidths, { width: this.field.width })
            if (width && width.width !== 'full') parts.push(width.label)
            if (this.field.required) parts.push(this.$t('Required'))
            return parts.join(' · ')
        },
        isAlwaysRequired() {
            return this.isFixed && FIXED_ALWAYS_REQUIRED.includes(this.field.key)
        },
        isRemovable() {
            return isRemovableField(this.field)
        },
        // The choices of a fixed select (salutation, slot) come from the booker / the event.
        hasOptions() {
            return !this.isFixed && FIELD_TYPES_WITH_OPTIONS.includes(this.field.type)
        },
        // A placeholder only makes sense where the attendee types something.
        hasPlaceholder() {
            return !this.isFixed && ['text', 'textarea', 'number'].includes(this.field.type)
        },
        optionMarkerClass() {
            switch (this.field.type) {
                case 'radio':
                    return 'el-icon-fa-circle far'
                case 'checkbox_group':
                    return 'el-icon-fa-square far'
                default:
                    return 'el-icon-fa-minus fas'
            }
        },
    },
    methods: {
        update(key, value) {
            this.registrationTemplateStore.setFieldValue({ index: this.index, key, value })
        },
        move(direction) {
            this.registrationTemplateStore.moveField(this.index, direction)
        },
        remove() {
            this.registrationTemplateStore.removeField(this.index)
        },
        addOption() {
            this.registrationTemplateStore.addOption(this.index)
        },
        removeOption(optionIndex) {
            this.registrationTemplateStore.removeOption({ fieldIndex: this.index, optionIndex })
        },
        updateOption(optionIndex, value) {
            this.registrationTemplateStore.setOptionLabel({ fieldIndex: this.index, optionIndex, value })
        },
    },
}
</script>

<style lang="sass" scoped>
    .field-card
        border: 1px solid $--border-color-base
        border-radius: 6px
        background: #fff
        &.is-locked
            border-style: dashed
            .field-card-head
                background: #fafafa
            .field-card-type i
                color: $color-grey-text

    .field-card-head
        display: flex
        align-items: center
        gap: 10px
        padding: 8px 12px
        background: #f5f7fa
        border-radius: 6px
        cursor: pointer
        user-select: none
        .field-card-grip
            flex: 0 0 10px
            color: #c0c4cc
            cursor: grab
            &:hover
                color: $--color-primary
            &:active
                cursor: grabbing
        .field-card-toggle
            flex: 0 0 10px
            font-size: 10px
            color: $color-grey-text
            transition: transform 0.15s ease
        .field-card-type
            display: flex
            align-items: center
            gap: 10px
            flex: 1
            min-width: 0
            .field-card-icon
                flex: 0 0 16px
                text-align: center
                color: $--color-primary
            .field-card-text
                min-width: 0
                line-height: 1.3
            .field-card-label
                font-size: 13px
                font-weight: 600
                color: #303133
                overflow: hidden
                text-overflow: ellipsis
                white-space: nowrap
            .field-card-meta
                font-size: 11px
                color: $color-grey-text
                overflow: hidden
                text-overflow: ellipsis
                white-space: nowrap
        // compact 1/1 · 1/2 · 1/3 switch, narrow enough to share a third-width card's head
        .width-control
            flex: 0 0 auto
            margin-left: 6px
            ::v-deep .el-radio-button__inner
                padding: 5px 7px
                font-size: 11px
        .field-card-actions
            display: flex
            align-items: center
            flex: 0 0 auto
            gap: 2px
            .action
                padding: 4px 5px
                color: #909399
                &:hover
                    color: $--color-primary
                &.is-disabled
                    color: #dcdfe6
                &.danger:hover
                    color: $--color-danger
            .el-button + .el-button
                margin-left: 0
    .field-card.is-expanded
        .field-card-head
            border-bottom: 1px solid $--border-color-lighter
            border-radius: 6px 6px 0 0
        .field-card-toggle
            transform: rotate(90deg)

    .field-card-body
        padding: 14px 14px 10px

    .el-form-item.compact
        margin-bottom: 12px
        ::v-deep .el-form-item__label
            padding-bottom: 2px
            line-height: 1.6
            font-size: 12px
        &.no-margin
            margin-bottom: 0

    .option-list
        display: flex
        flex-direction: column
        gap: 6px
        .option-row
            display: flex
            align-items: center
            gap: 8px
            .option-marker
                width: 14px
                text-align: center
                color: $color-grey-text
        .add-option
            align-self: flex-start
            margin-top: 2px
</style>
